import type { OrderComplete } from '@/features/orders/domain/model/order';
import type { PrintChannel } from '@/features/printing/domain/service/print-channels';
import type { PrintingActions } from '@/features/printing/presentation/providers/printing-provider';
import type { TakeOrderFlowEvent } from '@/features/totems/domain/model/take-order-flow-events';

export type TotemPrinterTarget = { ip: string; port: number };

/** Dependencias de los otros flujos; se leen en el momento de despachar, no al crear el router. */
export type TakeOrderFlowDeps = {
  clearCart: () => void;
  /** Cola de impresión del dashboard; `null` en la carta/tótem público (sin provider). */
  printing: PrintingActions | null;
  /** `false` mientras el provider resuelve impresoras: evita imprimir sin canales configurados. */
  isPrintingReady: boolean;
  /** Impresora del tótem físico para el driver local (solo páginas públicas). */
  totemPrinter: TotemPrinterTarget | null;
  printTotemDriver: (order: OrderComplete, printer: TotemPrinterTarget) => Promise<Response>;
};

export type TakeOrderFlowDispatcher = (event: TakeOrderFlowEvent) => void;

const DEFAULT_CHANNELS: PrintChannel[] = ['cocina', 'caja'];

/**
 * Traduce hechos del flujo de pago en comandos sobre los otros flujos.
 *
 * - `checkout_completed` → imprime (si hay orden y canales) y vacía el carrito, una sola vez por pedido.
 * - `order_created` / `checkout_abandoned` → sin efecto en otros flujos (reservados).
 */
export function createEventDispatcher(getDeps: () => TakeOrderFlowDeps): TakeOrderFlowDispatcher {
  const completedOrders = new Set<string>();

  const printCompletedOrder = (
    event: Extract<TakeOrderFlowEvent, { type: 'checkout_completed' }>,
    deps: TakeOrderFlowDeps
  ) => {
    if (!event.order) return;

    if (deps.printing) {
      const channels = event.channels ?? DEFAULT_CHANNELS;
      if (channels.length === 0 || !deps.isPrintingReady) return;
      void deps.printing.printOrder(event.order, { channels });
      return;
    }

    // Carta y tótem público: no hay cola de impresión, se usa el driver local del tótem.
    if (deps.totemPrinter) {
      const printer = deps.totemPrinter;
      void deps.printTotemDriver(event.order, printer).catch(error => {
        console.error('[take-order-flow] Error al imprimir en el driver del tótem', error);
      });
    }
  };

  return event => {
    switch (event.type) {
      case 'checkout_completed': {
        const orderId = event.order?.id;
        if (orderId) {
          if (completedOrders.has(orderId)) return;
          completedOrders.add(orderId);
        }

        const deps = getDeps();
        printCompletedOrder(event, deps);
        deps.clearCart();
        return;
      }

      case 'order_created':
      case 'checkout_abandoned':
        // Reservados: no hay efecto en carrito ni impresión.
        return;
    }
  };
}
